import { removeTrailingSlash } from "@brexhq/shared/utils/urlFiltering/urlFormatting";

import redirects from "./redirects/generated.json";

export const checkInternalLink = (
  to: string,
): { isInternalLink: boolean; link: string } => {
  if (!to) {
    return {
      isInternalLink: false,
      link: "/",
    };
  }
  let link = returnLink(to);
  const url = new URL(
    link,
    !link.startsWith("http") ? "https://www.brex.com" : undefined,
  );

  const isInternalLink = link.includes(".pdf")
    ? false
    : /^\/(?!\/)/.test(link) ||
      link.startsWith("#") ||
      // lets treat dashboard as internal and all other subdomains as external for now
      url.hostname.includes("dashboard.brex.com");

  if (isInternalLink) {
    const linkNoTrailingSlash = removeTrailingSlash(link);
    const redirectedLink =
      redirects[to] ?? redirects[link] ?? redirects[linkNoTrailingSlash];
    if (redirectedLink) {
      link = redirectedLink;
    }
  }

  //Previous logic in this file could produce an empty string link, if thats the case we set it to the index page
  if (link === "") link = "/";

  return {
    isInternalLink,
    link,
  };
};

/**
 * Function for checking whether the url ends with a file type (end of path has a . in it)
 * @param to - url string
 * @returns whether url string is a file type (pdf, svg, html, etc.)
 */
export const isFileLink = (to: string) => {
  // this url is not passed anywhere so relative paths can all have brex.com as base url
  const url = new URL(to, "https://www.brex.com");
  const regex = /\/[^/]*\.[^/]*$/gi;
  return regex.test(url.pathname);
};

/**
 * Helper function for splitting a link brex.com/link and returning /link to pass to link component for client side routing
 * @param link the link
 * @returns a string to pass to a link or a tag
 */

//NOTE: We used to force trailing slashes on all links - internal or external. This is no longer needed. Next.js will handle all redirects regardless of trailing slash
export const returnLink = (link: string): string => {
  const trimmed = link?.trim() ?? "";
  if (trimmed === "") return trimmed;

  // Pass mailto links through
  if (trimmed.includes("mailto")) {
    return trimmed;
  }

  // If it's a fully-qualified URL, decide based on hostname
  try {
    const url = new URL(trimmed);
    const hostname = url.hostname.toLowerCase();
    if (hostname === "brex.com" || hostname === "www.brex.com") {
      // Convert to path-only for main site
      return `${url.pathname}${url.search}${url.hash}` || "/";
    }
    // Keep subdomains and non-brex hosts as-is
    return trimmed;
  } catch {
    // Not a fully-qualified URL. Handle bare domains like "brex.com/foo" or "www.brex.com/foo"
    const normalized = trimmed.replace(/^https?:\/\//i, "");
    const mainDomainMatch = /^(?:www\.)?brex\.com(\/|$)/i.test(normalized);
    if (mainDomainMatch) {
      return normalized.replace(/^(?:www\.)?brex\.com/i, "") || "/";
    }
    // For subdomains or anything else, return as-is
    return trimmed;
  }
};

/**
 * Helper function for appending current page query params to a link
 * @param link the link
 * @param search the query params (normally passed in from useLocation())
 * @returns a link with query params appended
 */

export const addQueryParamsToLink = (link: string, search?: string): string => {
  const urlContainsAnchor = link.includes("#");
  if (!search && !urlContainsAnchor) return link;
  if (urlContainsAnchor) {
    const urlParts = link.split("#");
    const anchor = urlParts[1];
    const path = urlParts[0];
    return `${path + search}#${anchor}`;
  }

  return link + search;
};
